import React, { useState } from 'react';
import * as Yup from 'yup';
import { useLocation } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Stack, TextField } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { makeStyles } from '@material-ui/core';
import { keys } from 'lodash';
import http from '../../../config/http';
import { AUTH_POST_REGISTER_URL } from '../../../config/api';
import { _route } from '../../../utils';
import PhoneNumber from '../../../components/common/PhoneNumber';

const useStyles = makeStyles(() => ({
  textField: {
    padding: '0px!important',
    fontSize: '16px!important',
    fontFamily: 'Inter !important',
    fontWeight: '400',
    marginTop: '8px!important',
    transition: '.3s',
    borderRadius: '8px!important',
    '& .MuiInputLabel-formControl': {
      fontSize: '14px',
      lineHeight: '1em',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      // border: '1px solid #5433FF',
    },
    '& .MuiFormHelperText-root': {
      letterSpacing: '0 !important',
      fontFamily: 'Inter',
      margin: '6px 0 0 0 !important',
      fontWeight: '500',
    },
    '& .MuiOutlinedInput-root .MuiInputBase-input': {
      borderRadius: '8px',
      background: '#fff',
      fontSize: '14px !important',
      // border: '1px solid #D0D5DD',
      boxShadow: ' 0px 1px 2px rgb(16 24 40 / 5%)',
      padding: '13.5px 14px',
      transition: '.3s',
      '&:focus': {
        transition: '.3s',
        outline: '0',
        boxShadow: 'inset 0 1px 1px rgb(0 0 0 / 8%), 0 0 8px rgb(102 175 233 / 60%)',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: '1px !important',
    },
  },
  phoneNumberClass: {
    padding: '0px!important',
    fontSize: '16px!important',
    fontFamily: 'Inter !important',
    fontWeight: '400',
    marginTop: '0px!important',
    transition: '.3s',
    borderRadius: '8px!important',
  },
  borderClass: {
    '&.react-tel-input .form-control': {
      borderRadius: '8px',
    },
  },
  buttonClass: {
    '&.flag-dropdown': {
      borderTopLeftRadius: '8px',
      borderBottomLeftRadius: '8px',
      background: '#fff',
    },
  },
}));

const buttonStyles = {
  borderRadius: '10px',
  boxShadow: '0 4px 11px rgb(0 0 0 / 11%)',
  width: '100%',
  padding: '10px 14px',
  color: '#fff',
  transition: '0.2s',
  marginTop: '5px',
  fontWeight: '500',
  backgroundColor: '#5433ff',
  // borderColor: '#5433ff',
  // border: '1px solid #5433ff',
  fontFamily: 'Inter',
  letterSpacing: '0',
  textTransform: 'inherit',
  '&:hover': {
    backgroundColor: '#5433FF',
    borderColor: '#5433FF',
    opacity: '0.9',
    boxShadow: '0 4px 11px rgb(0 0 0 / 11%)',
  },
  '&:focus': {
    backgroundColor: '#5433FF',
    borderColor: '#5433FF',
    opacity: '0.9',
    boxShadow: '0 4px 11px rgb(0 0 0 / 11%)',
  },
  '&:visited': {
    backgroundColor: '#5433FF',
    borderColor: '#5433FF',
    opacity: '0.9',
    boxShadow: '0 4px 11px rgb(0 0 0 / 11%)',
  },
};

export default function RegisterForm() {
  const classes = useStyles();
  const [isValidMobile, setIsValidMobile] = useState(false);
  const [mobile, setMobile] = useState({ number: null, dialCode: '+91' });
  const routeParams = useLocation().search;

  const RegisterSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: RegisterSchema,
    onSubmit: async (e, { setSubmitting }) => {
      const searchParamsData = new URLSearchParams(routeParams);
      const source = { autopilot: searchParamsData.get('autopilot') || false };
      if (!keys(source).length === 0 || !keys(source).includes('autopilot')) {
        if (document.referrer.indexOf('autopilot') > -1) {
          source.utm_source = 'autopilot';
        }
      }

      const payload = {
        email: values.email,
        mobile: mobile.number,
        countryCode: mobile.dialCode,
        routeParams: source,
      };

      try {
        await http.post(AUTH_POST_REGISTER_URL, payload).then((res) => {
          if (res.data.status === 'success') {
            window.location = _route('verifyEmail', { emailId: values.email });
          } else {
            toast.warn(res.data.message);
          }
        });
      } catch (err) {
        toast.error(err.response.data.message);
      }
      setSubmitting(false);
    },
  });

  const { errors, touched, handleSubmit, values, isSubmitting, getFieldProps } = formik;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3} style={{ textAlign: 'left' }}>
          <div className="customLabel">Email *</div>
          <TextField
            fullWidth
            type="email"
            placeholder="abc@email.com"
            {...getFieldProps('email')}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
            InputLabelProps={{ shrink: false }}
            className={classes.textField}
          />
          <div className="customLabel mgTop">Phone number *</div>
          <div className={classes.phoneNumberClass}>
            <PhoneNumber
              handleDialCode={(val) => setMobile({ ...mobile, dialCode: val })}
              handlePhoneNumber={(number, isValid) => {
                setMobile({ ...mobile, number: number });
                setIsValidMobile(isValid);
              }}
            />
          </div>

          {/* <p className="sectionPara" style={{ marginTop: '1rem' }}>
            Note: We respect your privacy.{' '}
            <a
              underline="always"
              color="text.primary"
              href="https://www.sellergeni.com/terms-and-privacy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Read more
            </a>
            .
          </p> */}

          <LoadingButton
            disabled={!isValidMobile}
            fullWidth
            size="large"
            type="submit"
            sx={buttonStyles}
            variant="contained"
            loading={isSubmitting}
          >
            Create Account
          </LoadingButton>
        </Stack>
      </Form>
    </FormikProvider>
  );
}
